import {
    Box,
    Button,
    Chip,
    Container,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Skeleton,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import UserCard from '../../../components/card/UserCard';
import { getAllUsers } from '../../../data/api/users/UserRoutes';
import { getRoles } from '../../../data/api/roles/RoleRoutes';
import useIndex from '../../../hooks/useIndex';
import UserProfile from './UserProfile';
import CreateUserModal from './CreateUserModal';
import AssignMovesModal from '../../../components/modal/AssignMovesModal';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import styled from 'styled-components';
import { useDebounce } from 'react-use';
import validErrors from '../../../hooks/validErrors';
import { ROLE_IDS, isAdmin, isCompany } from '../../../config/roles';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: '#fff',
        '& fieldset': {
            borderColor: 'rgba(15, 34, 77, 0.14)',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#d6aa3a',
        },
    },
};

const UsersPage = () => {
    const { auth, token } = useIndex();
    const { uuid } = useParams();

    const admin = isAdmin(auth);
    const empresa = isCompany(auth);

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(12);
    const [data, setData] = useState(null);
    const [users, setUsers] = useState(null);
    const [userSearch, serUserSearch] = useState('');
    const [load, setLoad] = useState(true);
    const [openCreate, setOpenCreate] = useState(false);
    const [assignTarget, setAssignTarget] = useState(null);

    const [roleFilter, setRoleFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [roles, setRoles] = useState([]);
    const [companies, setCompanies] = useState([]);

    const [isReady, cancel] = useDebounce(() => {
        setPage(1);
        loadData(rowsPerPage, 1, userSearch);
    }, 1000, [userSearch]);

    useEffect(() => {
        cancel();
    }, []);

    useEffect(() => {
        if (!uuid) {
            loadData(rowsPerPage, page, userSearch);
        }
    }, [page, rowsPerPage, roleFilter, companyFilter, uuid]);
    // `uuid` está en la lista para cubrir el caso de "Volver" desde el
    // perfil: cuando el usuario sale del UserProfile el uuid pasa a
    // undefined y necesitamos refrescar el listado, sino el skeleton
    // queda colgado en `load=true` (estado inicial) sin nadie que lo
    // baje. También cubre el caso de entrar directo por URL a
    // /users/:uuid → volver: la primera carga la salta el `if (!uuid)`.

    useEffect(() => {
        if (admin) {
            loadRoles();
            loadCompanies();
        }
    }, [admin]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(1);
    };

    const buildFilters = () => {
        const filters = {};
        if (roleFilter) filters.role_id = roleFilter;
        if (companyFilter) filters.company_id = companyFilter;
        return filters;
    };

    const loadData = async (rpp, p, us) => {
        setLoad(true);

        try {
            const { data } = await getAllUsers(token, rpp, p, us, buildFilters());
            if (data?.success) {
                setData(data);
                setUsers(data?.data);
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setLoad(false);
        }
    };

    const loadRoles = async () => {
        try {
            const { data } = await getRoles(token);
            setRoles(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        }
    };

    const loadCompanies = async () => {
        try {
            const { data } = await getAllUsers(token, 100, 1, '', {
                role_id: ROLE_IDS.EMPRESA,
            });
            setCompanies(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        }
    };

    if (uuid) return <UserProfile uuid={uuid} />;

    const totalLabel = empresa ? 'Distribuidores' : 'Usuarios';

    return (
        <PageContainer maxWidth={false}>
            <FiltersCard>
                <FiltersGrid>
                    <TextField
                        size="small"
                        placeholder="Buscar..."
                        value={userSearch}
                        onChange={(e) => serUserSearch(e.target.value)}
                        sx={fieldStyles}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {admin && (
                        <>
                            <FormControl size="small" sx={fieldStyles}>
                                <InputLabel>Rol</InputLabel>
                                <Select
                                    label="Rol"
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {roles.map((r) => (
                                        <MenuItem key={r.id} value={r.id}>
                                            {r.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={fieldStyles}>
                                <InputLabel>Empresa</InputLabel>
                                <Select
                                    label="Empresa"
                                    value={companyFilter}
                                    onChange={(e) => setCompanyFilter(e.target.value)}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    {companies.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    )}

                    <FormControl size="small" sx={fieldStyles}>
                        <Select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                            <MenuItem value={12}>12</MenuItem>
                            <MenuItem value={24}>24</MenuItem>
                            <MenuItem value={48}>48</MenuItem>
                        </Select>
                    </FormControl>
                </FiltersGrid>

                <Box className="filters-header">

                    <Box className="filters-info">
                        <Chip label={`${totalLabel}: ${data?.meta?.total ?? 0}`} size="small" />
                        <Chip label={`Página: ${page}/${data?.meta?.last_page ?? 1}`} size="small" variant="outlined" />

                        <Pagination
                            count={data?.meta?.last_page ?? 1}
                            page={page}
                            onChange={handleChangePage}
                            size="small"
                        />
                    </Box>

                    {(admin || empresa) && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenCreate(true)}
                            size="small"
                            sx={{
                                height: '34px',
                                padding: '0 16px',
                                borderRadius: '12px',
                                fontWeight: 900,
                                letterSpacing: '0.4px',
                                textTransform: 'uppercase',
                                background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                boxShadow: '0 6px 14px rgba(15, 34, 77, 0.22)',

                                '&:hover': {
                                    background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                    boxShadow: '0 8px 18px rgba(15, 34, 77, 0.28)',
                                },

                                '&:active': {
                                    transform: 'scale(0.97)',
                                    boxShadow: '0 4px 10px rgba(15, 34, 77, 0.2)',
                                }
                            }}
                        >
                            Crear
                        </Button>
                    )}
                </Box>
            </FiltersCard>

            <SectionTitle>
                <span>Listado</span>
            </SectionTitle>

            <UsersGrid>
                {load ? (
                    [1, 2, 3].map((item) => (
                        <SkeletonCard key={item}>
                            <Skeleton variant="circular" width={50} height={50} />
                            <Skeleton width="80%" />
                        </SkeletonCard>
                    ))
                ) : users?.length ? (
                    users.map((user) => (
                        <UserCard
                            key={user.uuid}
                            user={user}
                            onAssignMoves={setAssignTarget}
                            onChanged={() => loadData(rowsPerPage, page, userSearch)}
                        />
                    ))
                ) : (
                    <EmptyState>
                        <FilterAltOutlinedIcon />
                        <Typography>No hay datos</Typography>
                    </EmptyState>
                )}
            </UsersGrid>

            <CreateUserModal open={openCreate} onClose={() => setOpenCreate(false)} />
            <AssignMovesModal
                open={!!assignTarget}
                onClose={() => setAssignTarget(null)}
                targetUser={assignTarget}
                onSuccess={() => {
                    setAssignTarget(null);
                    loadData(rowsPerPage, page, userSearch);
                }}
            />
        </PageContainer>
    );
};

export default UsersPage;

/* ================= STYLES ================= */

const PageContainer = styled(Container)`
    padding: 0.8rem !important;
`;

const FiltersCard = styled.div`
    padding: 0.5rem;
    border-radius: 12px;
    background: #fff;
    margin-bottom: 0.6rem;

    .filters-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 0.4rem;
        gap: 0.5rem;
    }

    .filters-info {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        flex-wrap: wrap;
    }

    @media (max-width: 700px) {
        padding: 0.75rem;

        .filters-header {
            flex-direction: column;
            align-items: stretch;
            gap: 0.8rem;
            margin-top: 0.8rem;
        }

        .filters-info {
            display: grid;
            grid-template-columns: repeat(2, max-content);
            justify-content: center;
            align-items: center;
            gap: 0.5rem;
            width: 100%;
        }

        .filters-info .MuiPagination-root {
            grid-column: 1 / -1;
            display: flex;
            justify-content: center;
            width: 100%;
        }

        .filters-info .MuiPagination-ul {
            flex-wrap: nowrap;
            justify-content: center;
        }

        .filters-header .MuiButton-root {
            width: 100%;
            height: 42px;
        }
    }
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: 1.5fr 1fr 1fr 0.7fr;
    gap: 0.4rem;

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.55rem;

        > .MuiTextField-root {
            grid-column: 1 / -1;
        }

        > .MuiFormControl-root {
            width: 100%;
            min-width: 0;
        }

        > .MuiFormControl-root:last-child {
            grid-column: 1 / -1;
        }

        .MuiInputBase-root {
            min-height: 42px;
        }
    }
`;

const SectionTitle = styled.div`
    text-align: center;
    margin: 0.6rem 0;
    font-weight: 900;
`;

const UsersGrid = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const SkeletonCard = styled.div`
    width: 200px;
    padding: 0.6rem;
    border-radius: 12px;
    background: #fff;
`;

const EmptyState = styled.div`
    text-align: center;
    width: 100%;
    padding: 1rem;
`;